<template>
    <form-validation v-slot="{ bindings }">
        <div class="custom-settings">
            <div class="custom-settings__description">
                <slot name="description" />
            </div>

            <div class="custom-settings__form">
                <slot name="form" />
            </div>

            <div class="custom-settings__actions">
                <slot name="actions">
                    <flex-container
                        align-items="center"
                        justify-content="flex-end"
                        :padding="Size.sm"
                    >
                        <slot name="buttons" />

                        <button-block :disabled="!bindings.isValid.value" @loading="saveNode">
                            Opslaan
                        </button-block>
                    </flex-container>
                </slot>
            </div>
        </div>
    </form-validation>
</template>

<script setup lang="ts">
    import {
        FlexContainer,
        ButtonBlock,
        ButtonState,
        type ButtonStateChangedEvent,
        Size,
    } from '@keyvue/one-ui';

    import { inject } from 'vue';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';

    interface Props {
        formData: Record<string, any>
        saveMessage: string
    }

    const props = withDefaults(defineProps<Props>(), {
        saveMessage: 'Wijzigingen opgeslagen',
    });

    const { notifySaving, notifySaved, notifyError } = useNotify();
    const parentStopEditingNode = inject<() => void>('stopEditingNode');
    const parentSaveFlow = inject<() => Promise<void>>('saveFlow');
    const parentApplyOptions = inject<(formData: Record<string, any>) => void>('applyOptions');

    async function saveNode(event: ButtonStateChangedEvent) {
        if (!parentApplyOptions || !parentSaveFlow) {
            event.button.setState(ButtonState.content);
            return;
        }

        try {
            const closeSavingNotification = notifySaving(undefined, 0);
            parentApplyOptions(props.formData);
            await parentSaveFlow();
            event.button.setState(ButtonState.completed);

            if (parentStopEditingNode) {
                parentStopEditingNode();
            }

            closeSavingNotification();
            notifySaved(undefined, props.saveMessage);
        } catch (error) {
            event.button.setState(ButtonState.content);
            notifyError(error);
        }
    }
</script>

<style scoped lang="scss">
.custom-settings {
    height: 100%;
    display: grid;
    grid-template-rows: min-content auto min-content;
    grid-template-columns: 100%;

    &__description {
        padding: size(md);
        border-bottom: solid 1px color(gray-300);
        background-color: var(--theme-colors-default--primary);
        color: white;

        :deep(p) {
            padding-top: size(sm);
        }
    }

    &__form {
        max-width: 100%;
        padding: size(md);
    }
}
</style>
